import { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Row, Col } from "react-bootstrap";
import { useDispatch } from "react-redux";

import { HeroHeading } from "style/components/HeroHeading";
import { Button } from "components/Button";
import { PortfolioBox } from "./PortfolioBox";
import { ManageInvestmentsContainer } from "./ManageInvestments.styles";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { updateTradingConnectionAllocations } from "features/Dashboard/service";
import { Loader } from "components/Loader";
import { validateAllocationAmount } from "features/Dashboard/utils";
import { useCommunityStrategies } from "features/Strategy/hook/useCommunityStrategies";

export const ManageInvestmentsModal = ({
  show,
  onHide,
  handleConnectAnotherPortfolio,
  strategy,
}) => {
  const dispatch = useDispatch();
  const { refetch: refetchActiveStrategies } = useCommunityStrategies({
    type: "active",
    enabled: false,
  });

  const [brokers, setBrokers] = useState([]);
  const [allAllocation, setAllAllocation] = useState([]);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    setBrokers(strategy?.broker_accounts);
  }, [strategy?.broker_accounts]);

  const handleConnectPortfolio = () => {
    onHide();
    handleConnectAnotherPortfolio();
  };

  const handleStoppedStrategy = (inx) => () => {
    const allBrokers = [...brokers];
    allBrokers.splice(inx, 1);
    setBrokers(allBrokers);
    refetchActiveStrategies();
  };

  const handleAllocationChange =
    (brokerId, accountValue, totalCash) => (value) => {
      const { message, hasError } = validateAllocationAmount(
        value,
        totalCash,
        strategy?.minimum_investment
      );
      setErrorMessage(message);

      if (hasError) {
        return;
      }

      const allocationValues = [...allAllocation];
      const allocationInx = allocationValues.findIndex(
        (allocation) => allocation.broker_account === brokerId
      );
      if (!value) {
        allocationValues.splice(allocationInx, 1);
        setAllAllocation(allocationValues);
        return;
      }

      const dollars = Number(value);
      const newValue = (dollars * 100) / accountValue;

      const newPayload = {
        broker_account: brokerId,
        new_allocation: {
          [strategy.strategy_id]: newValue / 100,
        },
      };

      if (allocationInx !== -1) {
        allocationValues[allocationInx] = newPayload;
      } else {
        allocationValues.push(newPayload);
      }

      setAllAllocation(allocationValues);
    };

  const handleUpdate = async () => {
    try {
      setIsUpdating(true);
      await updateTradingConnectionAllocations(allAllocation);
      dispatch(
        toast({
          type: "info",
          body: "Allocations Updated Successfully!",
        })
      );
      onHide();
      refetchActiveStrategies();
    } catch (error) {
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <>
      <ManageInvestmentsContainer show={show} size="lg" onHide={onHide}>
        <HeroHeading
          heading="Set Allocation Amount"
          subheading={strategy.name}
        />

        <div className="body-content">
          <div className="box-wrapper">
            {brokers.map((broker, inx) => (
              <PortfolioBox
                errorMessage={errorMessage}
                strategy={strategy}
                broker={broker}
                key={inx}
                handleStoppedStrategy={handleStoppedStrategy(inx)}
                handleAllocationChange={handleAllocationChange(
                  broker.broker_account_id,
                  broker.account_value.raw_number,
                  broker.total_cash
                )}
              />
            ))}

            {brokers.length === 0 && <div>No portfolio found!</div>}
          </div>

          <Row className="footer-wrapper">
            <Col md={6}>
              <Button
                variant="outlined"
                className="connect-portfolio-btn"
                onClick={handleConnectPortfolio}
              >
                Connect another portfolio
              </Button>
            </Col>
            <Col md={6}>
              <Button onClick={handleUpdate} disabled={errorMessage !== ""}>
                {isUpdating && <Loader className="on-btn" />}Update
              </Button>
            </Col>
          </Row>
        </div>
      </ManageInvestmentsContainer>
    </>
  );
};

const StrategyPropTypes = PropTypes.shape({
  strategy_id: PropTypes.string,
  name: PropTypes.string,
  last_7_days: PropTypes.array,
  author: PropTypes.string,
  broker_accounts: PropTypes.array,
  invested_amount: PropTypes.string,
  minimum_investment: PropTypes.oneOfType([PropTypes.number, PropTypes.object]),
});

ManageInvestmentsModal.propTypes = {
  show: PropTypes.bool,
  onHide: PropTypes.func,
  handleConnectAnotherPortfolio: PropTypes.func,
  strategy: StrategyPropTypes,
  getActiveStrategies: PropTypes.func,
};
